// Hook that owns the STOMP/WebSocket subscription for live seatmap updates.
//
// Encapsulates connect/reconnect logic with exponential backoff, exposes the
// current connection state, and re-fetches the seatmap on every push.

import { useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import { WS_BASE_RETRY_DELAY_MS, WS_MAX_RETRIES } from '../config';
import { seatmapApi } from '../api/seatmapClient';
import type { SeatToGuestMap } from '../types';

interface UseStompSeatMapArgs {
  enabled: boolean;
  sessionId: string | null;
  eventId: string | undefined;
  onSeatMap: (map: SeatToGuestMap) => void;
}

interface UseStompSeatMapResult {
  client: Client | null;
  connected: boolean;
  /** True once the reconnect loop has given up (after WS_MAX_RETRIES). */
  retriesExhausted: boolean;
}

function wsUrl(): string {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${wsProtocol}://${window.location.host}/ws`;
}

export function useStompSeatMap({
  enabled,
  sessionId,
  eventId,
  onSeatMap,
}: UseStompSeatMapArgs): UseStompSeatMapResult {
  const clientRef = useRef<Client | null>(null);
  const [connected, setConnected] = useState(false);
  const [retriesExhausted, setRetriesExhausted] = useState(false);

  // Keep the latest callback in a ref so reconnects don't tear down the hook
  // when the consumer re-creates the function reference.
  const onSeatMapRef = useRef(onSeatMap);
  useEffect(() => { onSeatMapRef.current = onSeatMap; }, [onSeatMap]);

  useEffect(() => {
    if (!enabled || !sessionId || !eventId) return;
    if (clientRef.current) return;

    let attempts = 0;
    let cancelled = false;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
      if (cancelled) return;
      if (attempts >= WS_MAX_RETRIES) {
        // Give up; consumer can now activate the SSE fallback.
        setRetriesExhausted(true);
        return;
      }
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = WS_BASE_RETRY_DELAY_MS * Math.pow(2, attempts - 1);
      retryHandle = setTimeout(connect, delay);
    };

    const connect = () => {
      if (cancelled) return;
      attempts++;
      const client = new Client({
        brokerURL: wsUrl(),
        connectHeaders: {
          // JWT validated in STOMP CONNECT frame by SeatMapStompChannelInterceptor
          Authorization: `Bearer ${sessionStorage.getItem('seatmapToken') ?? ''}`,
        },
        reconnectDelay: 0,
        onConnect: () => {
          if (cancelled) return;
          setConnected(true);
          setRetriesExhausted(false);
          attempts = 0; // reset backoff on successful connect
          client.subscribe(`/topic/seatmapUpdate/${eventId}`, async () => {
            const map = await seatmapApi.fetchSeatToGuestMap(sessionId);
            if (map) {
              onSeatMapRef.current(map);
              sessionStorage.setItem('seatToGuestMap', JSON.stringify(map));
            }
          });
        },
        onStompError: () => {
          setConnected(false);
          scheduleReconnect();
        },
        onWebSocketClose: () => {
          setConnected(false);
          scheduleReconnect();
        },
      });
      client.activate();
      clientRef.current = client;
    };

    connect();

    return () => {
      cancelled = true;
      if (retryHandle) clearTimeout(retryHandle);
      if (clientRef.current) {
        clientRef.current.deactivate();
        clientRef.current = null;
      }
      setConnected(false);
    };
  }, [enabled, sessionId, eventId]);

  return { client: clientRef.current, connected, retriesExhausted };
}
